/**
 * Remediation Guidance API Client
 *
 * Provides functions to fetch remediation guidance from the backend.
 * Guides users on how to fix accessibility issues.
 */

import { apiGet, apiPost } from "./client.js";
import type {
  RemediationGuidance,
  FallbackRemediationGuidance,
  IssueGroupWithGuidance,
} from "@accessimonitor/shared";

/**
 * Get remediation guidance for a specific issue key.
 * Returns tailored guidance if mapped, otherwise returns fallback.
 */
export async function getRemediationGuidance(
  issueKey: string,
): Promise<{ issue_key: string; guidance: RemediationGuidance | FallbackRemediationGuidance }> {
  return apiGet(`/remediation/issue/${encodeURIComponent(issueKey)}`);
}

/**
 * List all mapped remediation guidance entries.
 * Useful for admin surfaces or documentation.
 */
export async function listRemediationGuidance(): Promise<{
  count: number;
  guidance: (RemediationGuidance | FallbackRemediationGuidance)[];
}> {
  return apiGet("/remediation/guidance/list");
}

/**
 * Enrich multiple issue groups with remediation guidance.
 * Takes issue objects and returns them with guidance attached.
 */
export async function enrichIssueGroupsWithGuidance(issueGroups: Array<{
  id: string;
  issue_key: string;
  title: string;
  count: number;
  severity?: string;
}>): Promise<{
  count: number;
  issue_groups: IssueGroupWithGuidance[];
}> {
  return apiPost("/remediation/enrich-issue-groups", {
    issue_groups: issueGroups,
  });
}

/**
 * Get remediation guidance coverage statistics.
 * Returns how many issue keys are mapped vs fallback.
 */
export async function getRemediationCoverageStats(issueKeys: string[]): Promise<{
  total: number;
  mapped: number;
  fallback: number;
  coverage_percentage: number;
}> {
  return apiPost("/remediation/coverage", {
    issue_keys: issueKeys,
  });
}
